import {useQuery} from '@apollo/client';
import * as React from 'react';
import {Link, Route, Routes} from 'react-router';

import {CourseDetailScreen} from './CourseDetailScreen';
import {UnitDetailScreen} from './UnitDetailScreen';

import {Course, PAGINA_CURSOS, mesclarCursos, parseColumnList, podeTerMais, posterDoCurso, statusDoCurso} from '../coursesList';
import {QUERY_STUDENT_COURSES} from '../studentQueries';
import {StudentTheme} from '../studentTheme';

interface Props {
  theme: StudentTheme;
  /** settings.screens.courses.columnList da conta, quando houver. */
  columnList?: string | null;
}

interface CoursesData {
  courses?: Course[] | null;
}

const CoursesList: React.FC<Props> = ({theme, columnList}) => {
  const {data, loading, error, fetchMore, refetch} = useQuery<CoursesData>(QUERY_STUDENT_COURSES, {
    variables: {page: 1},
  });

  const [pagina, setPagina] = React.useState(1);
  const [carregandoMais, setCarregandoMais] = React.useState(false);
  const [acabou, setAcabou] = React.useState(false);

  const cursos = data?.courses ?? [];
  const colunas = parseColumnList(columnList);

  // Botao em vez de rolagem automatica: a lista de cursos e curta e um clique
  // explicito evita depender de IntersectionObserver aqui.
  const carregarMais = async () => {
    if (carregandoMais || acabou) return;

    const proxima = pagina + 1;
    setCarregandoMais(true);

    try {
      const {data: novos} = await fetchMore({
        variables: {page: proxima},
        updateQuery: (anterior, {fetchMoreResult}) => {
          if (!fetchMoreResult?.courses?.length) return anterior;

          return {courses: mesclarCursos(anterior?.courses ?? [], fetchMoreResult.courses)};
        },
      });

      const vieram = novos?.courses?.length ?? 0;
      setPagina(proxima);
      if (!podeTerMais(novos?.courses ?? [])) setAcabou(true);
      if (vieram === 0) setAcabou(true);
    } catch {
      setAcabou(true);
    } finally {
      setCarregandoMais(false);
    }
  };

  if (loading && cursos.length === 0) {
    return <p className="student-screen__estado">Carregando…</p>;
  }

  if (error && cursos.length === 0) {
    return (
      <div className="student-screen__estado">
        <p>Não foi possível carregar os cursos.</p>
        <button type="button" className="student-app__retry" onClick={() => refetch()}>
          Tentar de novo
        </button>
      </div>
    );
  }

  if (cursos.length === 0) {
    return <p className="student-screen__estado">Nenhum curso disponível.</p>;
  }

  return (
    <div className="cursos">
      <div className="cursos__grade" style={{gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))`}}>
        {cursos.map((curso) => {
          const poster = posterDoCurso(curso);
          const status = statusDoCurso(curso.courseStatus);

          return (
            <Link
              key={curso.id}
              to={curso.id}
              className="curso-card curso-card__link"
              style={{backgroundColor: theme.body, borderColor: theme.border, color: theme.text}}
            >
              {poster ? (
                <img className="curso-card__poster" src={poster} alt="" loading="lazy" />
              ) : (
                <div className="curso-card__poster curso-card__poster--vazio" style={{backgroundColor: theme.border}}>
                  <i className="fa-light fa-graduation-cap" aria-hidden="true" />
                </div>
              )}

              <div className="curso-card__info">
                <h2 className="curso-card__titulo">{curso.title}</h2>

                {status && (
                  <span className="curso-card__status" style={{color: theme.primary}}>
                    {status}
                  </span>
                )}

                {curso.resume && (
                  <p className="curso-card__resumo" style={{color: theme.neutral}}>
                    {curso.resume}
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {!acabou && cursos.length >= PAGINA_CURSOS && (
        <button type="button" className="student-app__retry cursos__mais" onClick={carregarMais} disabled={carregandoMais}>
          {carregandoMais ? 'Carregando…' : 'Ver mais'}
        </button>
      )}
    </div>
  );
};

export const CoursesScreen: React.FC<Props> = (props) => (
  <Routes>
    <Route index element={<CoursesList {...props} />} />
    <Route path=":courseId" element={<CourseDetailScreen theme={props.theme} />} />
    <Route path=":courseId/aula/:unitId" element={<UnitDetailScreen theme={props.theme} />} />
  </Routes>
);
